import '../styles/slider.scss';
import React, { useState, useEffect, useRef } from 'react';
import { data } from '../services/data.ts';

const Slider = ({ brands }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [startX, setStartX] = useState(0);
  const [moveX, setMoveX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef(null);
  const trackRef = useRef(null);
  const visibleBrands = 5;
  const transitionDuration = 500;
  const slider_time = data.slider_time
  // Clone brands to create seamless loop
  const clonedBrands = [...brands, ...brands.slice(0, visibleBrands)];

  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => prev + 1);
    
    if (currentIndex >= brands.length - 1) {
      setTimeout(() => {
        setCurrentIndex(0);
        setIsTransitioning(false);
      }, transitionDuration);
    } else {
      setTimeout(() => setIsTransitioning(false), transitionDuration);
    }
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => (prev - 1 + brands.length) % brands.length);
    setTimeout(() => setIsTransitioning(false), transitionDuration);
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (slider) {
      const itemWidth = slider.firstChild?.clientWidth || 0;
      slider.style.transition = isTransitioning && !isDragging 
        ? `transform ${transitionDuration}ms ease` 
        : 'none';
      slider.style.transform = `translateX(-${currentIndex * itemWidth + (isDragging ? (startX - moveX) : 0)}px)`;
    }
  }, [currentIndex, isTransitioning, isDragging, startX, moveX]);

  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, slider_time); 
  
    return () => clearInterval(interval); 
  }, [currentIndex, isTransitioning]);
  

  // Touch and mouse event handlers
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setStartX(touch.clientX);
    setIsDragging(true);
  };

  const handleMouseDown = (e) => {
    setStartX(e.clientX);
    setIsDragging(true);
    e.preventDefault(); // Prevent text selection
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    setMoveX(touch.clientX);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setMoveX(e.clientX);
  };

  const handleEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    const movedBy = startX - moveX;
    const threshold = 50; // Minimum pixels to consider it a swipe
    
    if (movedBy > threshold) {
      nextSlide();
    } else if (movedBy < -threshold) {
      prevSlide();
    }
    
    // Reset values
    setStartX(0);
    setMoveX(0);
  };

  // Add event listeners for mouse up/leave when dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseUp = () => handleEnd();
    const handleMouseLeave = () => handleEnd();
    const handleTouchEnd = () => handleEnd();

    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, startX, moveX]);

  return (
    <section className="brands-slider-section">
      <div className="brands-slider-container">
        <button className="slider-button prev" onClick={prevSlide} aria-label="Previous">
          &lt;
        </button>
        
        <div 
          className="slider-viewport"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
        >
          <div className="slider-track" ref={sliderRef}>
            {clonedBrands.map((brand, index) => (
              <div 
                key={`${index}-${brand.name}`}
                className="slider-item"
                aria-hidden={index >= visibleBrands}
              >
                <div className="brand-image-container">
                  <img 
                    src={brand.image} 
                    alt={brand.name}
                    loading="lazy"
                  />
                </div>
                <div className="brand-caption">
                  {brand.name}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <button className="slider-button next" onClick={nextSlide} aria-label="Next">
          &gt;
        </button>
      </div>
    </section>
  );
};

export default Slider;